/**
 * <plus-cla-slider>
 * Scroll-snap slider for the "Collection list advanced" section.
 * CSS decides whether the track is a grid or a horizontal scroller at each screen size; this
 * element only adds arrows, dots, looping and optional autoplay while the track actually overflows.
 *
 * Markup: [data-track] (the scroller), any number of [data-prev] / [data-next] buttons, and an
 * optional [data-dots] container that is filled with one button per stop.
 */
const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const TOLERANCE = 4;

class PlusClaSlider extends HTMLElement {
  /** @type {HTMLElement | null} */
  track = null;
  /** @type {HTMLElement | null} */
  dotsContainer = null;
  /** @type {number[]} */
  stops = [];
  frame = 0;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  timer = undefined;
  paused = false;
  /** @type {ResizeObserver | null} */
  resizeObserver = null;

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.dotsContainer = this.querySelector('[data-dots]');
    if (!this.track) return;

    this.querySelectorAll('[data-prev]').forEach((button) => button.addEventListener('click', this.handlePrev));
    this.querySelectorAll('[data-next]').forEach((button) => button.addEventListener('click', this.handleNext));
    this.dotsContainer?.addEventListener('click', this.handleDotClick);
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });

    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('shopify:block:select', this.handleBlockSelect);
    this.addEventListener('shopify:block:deselect', this.resume);
    document.addEventListener('visibilitychange', this.handleVisibility);
    DESKTOP_QUERY.addEventListener('change', this.refresh);
    REDUCED_MOTION.addEventListener('change', this.refresh);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.refresh);
      this.resizeObserver.observe(this.track);
    }

    this.refresh();
  }

  disconnectedCallback() {
    this.querySelectorAll('[data-prev]').forEach((button) => button.removeEventListener('click', this.handlePrev));
    this.querySelectorAll('[data-next]').forEach((button) => button.removeEventListener('click', this.handleNext));
    this.dotsContainer?.removeEventListener('click', this.handleDotClick);
    this.track?.removeEventListener('scroll', this.handleScroll);

    this.removeEventListener('mouseenter', this.pause);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.pause);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.resume);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    DESKTOP_QUERY.removeEventListener('change', this.refresh);
    REDUCED_MOTION.removeEventListener('change', this.refresh);

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.stopAutoplay();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  get isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  get loop() {
    return this.dataset.loop === 'true';
  }

  get perMove() {
    if (!DESKTOP_QUERY.matches) return 1;
    return Math.max(1, Number(this.dataset.perMoveDesktop) || 1);
  }

  get behavior() {
    return REDUCED_MOTION.matches ? 'instant' : 'smooth';
  }

  /** Current distance scrolled from the start edge, positive in both directions. */
  get position() {
    return this.track ? Math.abs(this.track.scrollLeft) : 0;
  }

  get maxScroll() {
    return this.track ? Math.max(0, this.track.scrollWidth - this.track.clientWidth) : 0;
  }

  get overflowing() {
    return this.maxScroll > TOLERANCE;
  }

  /** Re-measures the stops, rebuilds the dots and syncs everything. */
  refresh = () => {
    if (!this.track) return;
    this.stops = this.measureStops();
    this.toggleAttribute('data-overflowing', this.overflowing);
    this.buildDots();
    this.sync();
    this.startAutoplay();
  };

  /** Scroll offsets the slider can rest on: every `perMove`-th slide, plus the very end. */
  measureStops() {
    const track = this.track;
    if (!track || !this.overflowing) return [0];

    const trackBox = track.getBoundingClientRect();
    const max = this.maxScroll;
    const rtl = this.isRtl;
    const step = this.perMove;
    /** @type {number[]} */
    const stops = [];

    Array.from(track.children).forEach((slide, index) => {
      if (index % step !== 0) return;
      const box = slide.getBoundingClientRect();
      const offset = rtl ? trackBox.right - box.right : box.left - trackBox.left;
      const stop = Math.min(max, Math.max(0, Math.round(offset + this.position)));
      if (!stops.some((existing) => Math.abs(existing - stop) <= TOLERANCE)) stops.push(stop);
    });

    if (!stops.some((existing) => Math.abs(existing - max) <= TOLERANCE)) stops.push(max);
    return stops.sort((a, b) => a - b);
  }

  buildDots() {
    const container = this.dotsContainer;
    if (!container) return;
    container.replaceChildren();
    if (this.stops.length < 2) return;

    this.stops.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'plus-cla__dot';
      dot.dataset.index = String(index);
      dot.setAttribute('aria-label', `Go to slide ${index + 1} of ${this.stops.length}`);
      container.append(dot);
    });
  }

  get activeIndex() {
    const position = this.position;
    let closest = 0;
    this.stops.forEach((stop, index) => {
      if (Math.abs(stop - position) < Math.abs(this.stops[closest] - position)) closest = index;
    });
    return closest;
  }

  sync() {
    const index = this.activeIndex;
    const atStart = this.position <= TOLERANCE;
    const atEnd = this.position >= this.maxScroll - TOLERANCE;
    const canLoop = this.loop && this.overflowing;

    this.querySelectorAll('[data-prev]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = !this.overflowing || (atStart && !canLoop);
    });
    this.querySelectorAll('[data-next]').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = !this.overflowing || (atEnd && !canLoop);
    });

    this.dotsContainer?.querySelectorAll('.plus-cla__dot').forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });
  }

  /** @param {number} index */
  goTo(index, behavior = this.behavior) {
    if (!this.track || !this.stops.length) return;
    const last = this.stops.length - 1;
    const target = Math.min(last, Math.max(0, index));
    const offset = this.stops[target] ?? 0;
    this.track.scrollTo({ left: this.isRtl ? -offset : offset, behavior });
  }

  /** @param {number} direction -1 for back, 1 for forward */
  move(direction, wrap = this.loop) {
    if (!this.overflowing) return;
    const last = this.stops.length - 1;
    const atStart = this.position <= TOLERANCE;
    const atEnd = this.position >= this.maxScroll - TOLERANCE;

    if (direction > 0 && atEnd) {
      if (wrap) this.goTo(0);
      return;
    }
    if (direction < 0 && atStart) {
      if (wrap) this.goTo(last);
      return;
    }

    const position = this.position;
    if (direction > 0) {
      const next = this.stops.findIndex((stop) => stop > position + TOLERANCE);
      this.goTo(next === -1 ? last : next);
    } else {
      let previous = 0;
      this.stops.forEach((stop, index) => {
        if (stop < position - TOLERANCE) previous = index;
      });
      this.goTo(previous);
    }
  }

  handlePrev = () => this.move(-1);

  handleNext = () => this.move(1);

  /** @param {Event} event */
  handleDotClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('.plus-cla__dot') : null;
    if (!(target instanceof HTMLElement)) return;
    this.goTo(Number(target.dataset.index) || 0);
  };

  handleScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.sync();
    });
  };

  /** @param {Event} event */
  handleBlockSelect = (event) => {
    this.pause();
    const track = this.track;
    if (!track || !(event.target instanceof Element)) return;
    const slide = Array.from(track.children).find((child) => child === event.target || child.contains(event.target));
    if (!(slide instanceof HTMLElement)) return;

    const trackBox = track.getBoundingClientRect();
    const box = slide.getBoundingClientRect();
    const offset = this.isRtl ? trackBox.right - box.right : box.left - trackBox.left;
    const left = Math.min(this.maxScroll, Math.max(0, offset + this.position));
    track.scrollTo({ left: this.isRtl ? -left : left, behavior: 'instant' });
  };

  /** @param {FocusEvent} event */
  handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.resume();
  };

  handleVisibility = () => {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  };

  pause = () => {
    this.paused = true;
  };

  resume = () => {
    this.paused = false;
  };

  startAutoplay() {
    this.stopAutoplay();
    if (this.dataset.autoplay !== 'true' || REDUCED_MOTION.matches || !this.overflowing) return;
    const seconds = Math.max(2, Number(this.dataset.interval) || 5);
    this.timer = setInterval(() => {
      if (!this.paused && !document.hidden) this.move(1, true);
    }, seconds * 1000);
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }
}

if (!customElements.get('plus-cla-slider')) {
  customElements.define('plus-cla-slider', PlusClaSlider);
}
